// apps/web/src/components/compras/fornecedor-combobox.tsx — Fase 13, Plano 17 (COMP-01, COMP-04).
// Seletor de fornecedor com busca (Popover + lista, molde de cliente-combobox.tsx).
// Consulta GET /api/v1/compras/lookups/fornecedores (compras:consultar; até 20 ativos ou a confirmar, inativos não aparecem).
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search, UserCheck } from 'lucide-react';
import type { FornecedorLookup } from '@pieltec/contracts';
import { formatarCnpj } from '../../lib/compras/cnpj';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface FornecedorComboboxProps {
  readonly value: FornecedorLookup | null;
  readonly onChange: (fornecedor: FornecedorLookup) => void;
  /**
   * Mostra os fornecedores "A confirmar" (criados pela NF). Padrão: sim, com o badge. Telas que não podem usar um
   * cadastro ainda não confirmado (ex.: novo pedido de compra) passam `false`.
   */
  readonly incluirAConfirmar?: boolean;
  readonly disabled?: boolean;
  readonly invalido?: boolean;
  readonly id?: string;
}

const ATRASO_BUSCA_MS = 250;
const ENDPOINT = '/api/v1/compras/lookups/fornecedores';

export function FornecedorCombobox({ value, onChange, incluirAConfirmar = true, disabled = false, invalido = false, id }: FornecedorComboboxProps) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [fornecedores, setFornecedores] = useState<readonly FornecedorLookup[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    const timer = window.setTimeout(() => {
      const termo = busca.trim();
      const query = termo ? `?busca=${encodeURIComponent(termo)}` : '';
      void fetch(`${ENDPOINT}${query}`, { credentials: 'include' })
        .then(async (res) => {
          if (!ativo) return;
          if (!res.ok) {
            setErro('Não foi possível carregar os fornecedores.');
            setFornecedores([]);
            return;
          }
          setFornecedores((await res.json()) as FornecedorLookup[]);
        })
        .catch(() => {
          if (ativo) setErro('Não foi possível carregar os fornecedores.');
        })
        .finally(() => {
          if (ativo) setCarregando(false);
        });
    }, ATRASO_BUSCA_MS);
    return () => {
      ativo = false;
      window.clearTimeout(timer);
    };
  }, [open, busca]);

  const visiveis = incluirAConfirmar ? fornecedores : fornecedores.filter((f) => f.situacao !== 'A_CONFIRMAR');

  return (
    <Popover
      open={open}
      onOpenChange={(proximo) => {
        setOpen(proximo);
        if (!proximo) setBusca('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={invalido || undefined}
          className={`ui-control flex min-h-[var(--control-height,2rem)] w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm text-slate-800 disabled:opacity-50 ${
            invalido ? 'border-rose-500' : 'border-slate-300'
          }`}
        >
          <span className="truncate">{value ? `${value.razaoSocial} (${formatarCnpj(value.cnpj)})` : 'Selecione o fornecedor...'}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 text-slate-400" aria-hidden="true" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="overflow-hidden p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          buscaRef.current?.focus();
        }}
      >
        <div className="flex items-center gap-2 border-b bg-slate-50 p-2">
          <Search className="size-3.5 shrink-0 text-slate-400" aria-hidden="true" />
          <input
            ref={buscaRef}
            type="text"
            aria-label="Buscar fornecedor"
            placeholder="Buscar por razão social ou CNPJ"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent text-sm text-slate-900 focus:outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1" role="listbox" aria-label="Fornecedores">
          {carregando ? (
            <div className="flex items-center justify-center gap-2 p-4 text-center text-sm text-slate-500">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Carregando fornecedores...
            </div>
          ) : erro ? (
            <div role="alert" className="p-4 text-center text-sm text-rose-700">
              {erro}
            </div>
          ) : visiveis.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">Nenhum fornecedor encontrado.</div>
          ) : (
            visiveis.map((fornecedor) => {
              const selecionado = value?.id === fornecedor.id;
              return (
                <button
                  key={fornecedor.id}
                  type="button"
                  role="option"
                  aria-selected={selecionado}
                  onClick={() => {
                    onChange(fornecedor);
                    setOpen(false);
                    setBusca('');
                  }}
                  className={`flex min-h-[var(--control-height,2rem)] w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    selecionado ? 'bg-slate-900 font-semibold text-white' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex min-w-0 flex-col pr-2">
                    <span className="truncate">{fornecedor.razaoSocial}</span>
                    <span className={`flex items-center gap-2 text-xs ${selecionado ? 'text-slate-300' : 'text-slate-500'}`}>
                      <span className="tabular-nums">{formatarCnpj(fornecedor.cnpj)}</span>
                      {fornecedor.situacao === 'A_CONFIRMAR' && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                          <UserCheck className="size-3 shrink-0" aria-hidden="true" />A confirmar
                        </span>
                      )}
                    </span>
                  </span>
                  {selecionado && <Check className="size-4 shrink-0" aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
